import fetchApi, { FetchError } from "../../../util/fetchApi";

/**
 * Starting and stopping runs, against the trainer plane. A variation launches
 * by its row (the anti-tamper rule: the request cannot override what curation
 * stored); without one, the bare experience at the named version — a staff
 * thing, since players only ever open variations.
 *
 * A launch is ACCEPTED at machine judgment (ADR-026) and returns with the run
 * in `starting`; placement guidance then rides the play surface's own session
 * as setup petitions, and coach parks the surface's dial until the experience
 * is resident. There is nothing to retry here.
 */

export interface LaunchRequest {
    variationKey?: string;
    experienceId?: string;
    experienceVersion?: string;
    sport?: string;
    /** Inputs the play surface collected at its own Start (`run.request`). */
    params?: unknown;
}

export interface LaunchedRun {
    runId: string;
    experienceId: string;
    experienceVersion: string;
}

/** What the trainer plane knows of the caller's pairing on a trainer. */
export interface CurrentRun {
    id: string;
    experienceId: string;
    variationKey: string | null;
    endedAt: string | null;
}

export async function launchRun(
    trainerId: number,
    request: LaunchRequest,
): Promise<LaunchedRun> {
    const { params, ...rest } = request;
    // A Start that carried the surface's own inputs is a front door of its
    // own (ADR-044): the run is born begun, so the player taps once. Without
    // inputs the run waits, and the surface's next Start is its begin.
    const body =
        params !== undefined
            ? { ...rest, params, origin: "api", beginNow: true }
            : rest;
    return fetchApi<LaunchedRun>(
        `/trainer/${trainerId}/api/experience/runs`,
        "POST",
        body,
        // Accept is fast, but a cold trainer's bundle fetch rides inside it
        // and can take ~30s — far past fetchApi's 10s default.
        { signal: AbortSignal.timeout(45_000) },
    );
}

/** Stop whatever run the trainer holds; answers the id it stopped, if any. */
export async function stopRun(
    trainerId: number,
): Promise<{ stopped: string | null }> {
    return fetchApi<{ stopped: string | null }>(
        `/trainer/${trainerId}/api/experience/runs/stop`,
        "POST",
        {},
    );
}

export async function currentRun(
    trainerId: number,
): Promise<CurrentRun | null> {
    const { run } = await fetchApi<{ run: CurrentRun | null }>(
        `/trainer/${trainerId}/api/experience/runs/current`,
    );
    return run;
}

/** Not failures of the launch: the trainer is off, asleep or between uplinks. */
const UNREACHABLE = new Set([404, 504]);

/** A refused launch, in words for the person holding the phone. */
export function launchFailureText(error: unknown): string {
    if (error instanceof FetchError) {
        if (UNREACHABLE.has(error.statusCode)) {
            return "The trainer isn't responding. Check that it's on, then try again.";
        }
        if (error.statusCode === 409) {
            return "The trainer is already running something. Stop it, then try again.";
        }
    }
    return "The trainer couldn't start this. Try again, or contact support if it keeps happening.";
}
